import { createToolPiezoArtifact } from './createToolPiezoArtifact';
import { isPiezoEnabledEnvironment } from './environment';
import { getTumblerConfig } from '../../../utils/appConfig';
import { getPiezoClient } from '../provisioning/PiezoClient';
import { getPiezoWorkerPool } from '../pool/workerPool';

/**
 * Describes a single migrated-tool invocation an operation will make. The browser signs these exact
 * parameters; the server builds the same arguments itself, so only the artifact is sent.
 */
export interface PiezoToolCall {
  /** Unique id for this call within the operation (used to look up the artifact on the server). */
  key: string;
  /** Mechanic tool path, e.g. 'aes/domain/cp/get-elasticsearch-domain-config'. */
  toolId: string;
  /** Exact tool parameters, signed as { parameters }. Must match what the server sends for this tool. */
  parameters: Record<string, unknown>;
}

/** One entry of the piezoArtifacts map: the operator-signed artifact. */
export interface PiezoArtifactEntry {
  artifact: string;
}

/** Map keyed by PiezoToolCall.key, carried in the GraphQL variables as `piezoArtifacts`. */
export type PiezoArtifactMap = Record<string, PiezoArtifactEntry>;

/**
 * Builds the piezoArtifacts map for an operation: signs each tool call's parameters with the
 * operator key and returns { key: { artifact } }. Best-effort — a call whose artifact
 * could not be created (e.g. no policy) is simply omitted, so the operation still proceeds.
 */
export async function createPiezoArtifactMap(
  calls: PiezoToolCall[],
): Promise<PiezoArtifactMap> {
  const map: PiezoArtifactMap = {};

  // Returning early skips init, pool warm-up and the sign fan-out, so an environment Piezo does not run
  // in pays nothing for artifacts the server would route past.
  if (!isPiezoEnabledEnvironment()) {
    return map;
  }

  // Make sure the pool is warm before fanning out. Normally a no-op: index.tsx already warms it on
  // operator entry and warmUp is idempotent. It matters on paths where that eager warm-up didn't run
  // (it sits inside `if (user)`), and it's how the unit tests control pool size.
  //
  // Not awaited — waiting here would stall the first user-visible query on warm-up. Signing already
  // falls back to the main thread whenever the pool isn't ready. initPromise is still passed through
  // because workers must not hydrate before the main thread has written the cache.
  const region = getTumblerConfig().current;
  const initPromise = getPiezoClient().init({ region: region.publicRegion });
  void getPiezoWorkerPool()
    .warmUp(initPromise, region.publicRegion, calls.length)
    .catch(() => undefined);

  await Promise.all(
    calls.map(async (call) => {
      const artifact = await createToolPiezoArtifact({
        toolId: call.toolId,
        parameters: call.parameters,
      });
      if (artifact) {
        map[call.key] = { artifact };
      }
    }),
  );
  return map;
}
